import React, { useEffect, useMemo, useState } from 'react';
import { SlateHeaderView } from 'apps/sigma2-ui/src/copilot/components/SlateHeaderView';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { PoolSubject } from 'libs/end-to-end/src/copilot';
import { FocusArea } from 'libs/end-to-end/src/focus-area';
import { GenerateVisualizationEvent } from 'libs/end-to-end/src/visualizer';
import { Scrollable } from 'apps/sigma2-ui/src/components/Scrollable';
import { SlatePoolSubject } from 'libs/end-to-end/src/copilot';
import { SlateBadges } from 'apps/sigma2-ui/src/copilot/components/SlateBadges';
import { Paragraph } from 'apps/sigma2-ui/src/components/ui/Paragraph';
import { useDimensionConfig } from 'apps/sigma2-ui/src/lib/hooks/useDimensionConfig';
import { CalibrationFraming } from 'libs/end-to-end/src/calibration-framing';
import { SlateSubject } from 'apps/sigma2-ui/src/copilot/components/SlateSubject';
import { BasicCopilotSortType } from 'libs/end-to-end/src/copilot-utils';
import { sortSubjects } from 'libs/end-to-end/src/copilot-utils';

interface SlateViewProps {
  allSubjectsWithSlateStatus: SlatePoolSubject[];
  addToSlate: (subject: PoolSubject) => void;
  removeFromSlate: (subject: PoolSubject) => void;
  selectedSubjectId: string | null;
  setSelectedSubjectId: (id: string | null) => void;
  resetSlate: () => void;
  selectedFilter: SlateViewFilter;
  setSelectedFilter: (filter: SlateViewFilter) => void;
  nextSubjectIdToScrollTo: string | null;
  focusArea: FocusArea;
  downloadableGenerateVisualizationEvent: GenerateVisualizationEvent | null;
  isFetchingShareLink: boolean;
  onFetchShareLink: () => void;
  isShareable: boolean;
  calibrationFraming: CalibrationFraming;
}

export type SlateViewFilter = 'your-slate';

export interface SlateViewFilterValueProps {
  tabValue: SlateViewFilter;
  displayedValue: string;
  description: string;
  automationTag?: string;
}

export const SlateView = (props: SlateViewProps) => {
  const language = useLanguage();

  const { isMinorityDimensionLevel } = useDimensionConfig();

  const [selectedSort, setSelectedSort] =
    useState<BasicCopilotSortType>('highest-to-lowest');

  const sortedSubjectsToDisplay = useMemo(() => {
    return sortSubjects(
      props.allSubjectsWithSlateStatus.filter((sub) => sub.isInSlate),
      selectedSort,
    );
  }, [props, selectedSort]);

  useEffect(() => {
    if (props.nextSubjectIdToScrollTo) {
      const element = document.getElementById(
        `copilotSlateSubjectView${props.nextSubjectIdToScrollTo}`,
      );
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center',
        });
      }
    }
  }, [props.nextSubjectIdToScrollTo]);

  const averageReadiness = useMemo(() => {
    if (sortedSubjectsToDisplay.length === 0) {
      return 0;
    }

    const sum = sortedSubjectsToDisplay.reduce(
      (sum, subject) => sum + subject.readinessRating,
      0,
    );
    return sum / sortedSubjectsToDisplay.length;
  }, [sortedSubjectsToDisplay]);

  return (
    <>
      <div className="p-4 bg-white">
        <SlateHeaderView
          filteredSubjectsToDisplay={sortedSubjectsToDisplay}
          resetSlate={props.resetSlate}
          focusArea={props.focusArea}
          downloadableGenerateVisualizationEvent={
            props.downloadableGenerateVisualizationEvent
          }
          isFetchingShareLink={props.isFetchingShareLink}
          onFetchShareLink={props.onFetchShareLink}
          isShareable={props.isShareable}
          calibrationFraming={props.calibrationFraming}
          selectedSort={selectedSort}
          setSelectedSort={setSelectedSort}
        />
      </div>
      <div className="bg-white py-4 px-4">
        <SlateBadges
          language={language}
          averageReadiness={averageReadiness}
        />
      </div>
      <Scrollable
        trackWidthClass={'w-4'}
        trackHandleWidthClass={'w-[10px]'}
        sneakyPeakyHeight={0}
        className="bg-white bottom-0 h-full"
      >
        <div>
          <div className={'py-2'}>
            {sortedSubjectsToDisplay.length === 0 && (
              <div className="p-4">
                <Paragraph className="py-1 font-black">
                  {props.focusArea === 'Hiring'
                    ? language.hiringCopilotEmptyStateTitle()
                    : language.promotionCopilotEmptyStateTitle()}
                </Paragraph>
                <Paragraph>
                  {props.focusArea === 'Hiring'
                    ? language.hiringCopilotEmptyStateDescription()
                    : language.promotionCopilotEmptyStateDescription()}
                </Paragraph>
              </div>
            )}
            {sortedSubjectsToDisplay.length > 0 &&
              sortedSubjectsToDisplay.map((subject) => {
                return (
                  <SlateSubject
                    subject={subject}
                    selectedSubjectId={props.selectedSubjectId}
                    setSelectedSubjectId={props.setSelectedSubjectId}
                    addToSlate={props.addToSlate}
                    removeFromSlate={props.removeFromSlate}
                    key={subject.id}
                    focusArea={props.focusArea}
                  />
                );
              })}
          </div>
        </div>
      </Scrollable>
    </>
  );
};
